import { Box, Loader, ModBox, Spacer, SpacerNew, Text, TextNew } from '@jfs/ui-new'
import { useEffect, useState } from 'react'
import { FlatList } from 'react-native'
import { TextInputWithCb } from '../../components/TextInputWithCb'
import { useLazyApiData, useStores } from '../../data/store'
import { clean, convertCamelCaseToString } from '../../utils'
import { ApprovalTypes } from '../../utils/constants'
import { getTextView } from '../EntityMaster/EntityDetails'
import { ApprovalOrDecline } from './ApproveOrDecline'
import { styles } from './styles/MasterApprovalsStyles'

interface IndividualItemProps {
  title: string
  value: string | number | boolean
}

interface ApprovalActionFormInterface {
  data: any
  type: string
  approvalData: any
  onSuccess: () => void
  showUpdatedData?: boolean
  showCurrentData?: boolean
  showAuditData?: boolean
}

const keyExtractorData = (item: any, index: number) => `${item}_${index}`

const IndividualItem = ({ title, value }: IndividualItemProps) => {
  const txtValue = title === 'Status' ? (value ? 'ENABLE' : 'DISABLE') : value

  return (
    <Box {...styles.individualItemWrapper}>
      <Text {...styles.cardTitleText} numberOfLines={1}>
        {title}
      </Text>
      <Spacer size={5} />
      <Box {...styles.dashedContainer}>
        <Text {...styles.cardInnerText}>{txtValue}</Text>
      </Box>
    </Box>
  )
}

const renderItem = (data: any, item: any, type: any, download: any) => {
  const fileDownload = () => {
    download && download(data.link, data.fileName, data?.fileName?.split('.')?.[0], data.fileType)
  }
  return typeof data[item] !== 'object' &&
    data[item] !== null &&
    (item !== 'type' || type === ApprovalTypes.BANKING_CODE) ? (
    <Box paddingVertical={8} marginRight={16} minWidth={175} f={1}>
      <TextInputWithCb
        label={convertCamelCaseToString(item)}
        value={data[item] ?? ''}
        showCheckbox={false}
        onPress={() => fileDownload()}
      />
    </Box>
  ) : null
}

const getEntityDetails = (data: any) => {
  const { info, endpoint, contactInfo, ipDetails, id } = data
  return (
    <Box>
      <Spacer direction="vertical" size={16} />
      <Box flexDirection="row" flexWrap="wrap" flexGrow={1}>
        {getTextView({ id: id })}
        {getTextView(info)}
        {getTextView(endpoint)}
        {contactInfo?.map((item: any, index: any) => {
          const cInfo = { ...item }
          delete cInfo.id
          return getTextView(cInfo)
        })}
        {ipDetails?.map((item: any, index: any) => {
          const cInfo = { ...item }
          const id = item.id
          delete cInfo.id
          return getTextView(cInfo)
        })}
      </Box>
    </Box>
  )
}

const renderListItem = (data: any, keys: any[], type?: string, downloadFile?: any) => {
  if (type === ApprovalTypes.ENTITY) {
    return getEntityDetails(data)
  }
  return (
    <Box {...styles.listItem}>
      <FlatList
        style={{ width: '100%', height: '100%' }}
        nativeID="hv-scroll"
        keyExtractor={keyExtractorData}
        horizontal={true}
        data={keys}
        renderItem={({ item }) => renderItem(data, item, type, downloadFile)}
        persistentScrollbar
        scrollEnabled
        showsHorizontalScrollIndicator
        showsVerticalScrollIndicator
      />
    </Box>
  )
}

export const ApprovalActionForm = ({
  data,
  type,
  approvalData,
  onSuccess,
  showUpdatedData = true,
  showCurrentData = true,
  showAuditData = false,
}: ApprovalActionFormInterface) => {
  const [isLoading, setLoading] = useState(true)
  const { approvalType, id, username } = approvalData
  const {
    getEntityById,
    getCardById,
    getNetbankingById,
    getMerchantById,
    getRoutingRuleById,
    entity,
    netbank,
    card,
    getBankingCodeById,
    merchant,
    getPackageById,
    getFeePackageById,
    getPackage2InsById,
    getPackage2MIdById,
    getFeePackageToMidById,
    packageData,
    packageInstrument,
    packageMId,
    getFilebyId,
    excelFile,
    bankingCode,
    fetchAllByFile,
    download,
    feePackageData,
    feePackageToMidData,
    merchantPayoutConfigData,
    crDrAdjustmentData,
    fetchMidPayoutConfig,
    payoutCmsData,
    riskConfigTxnData,
  } = useStores((store) => ({
    getEntityById: store.entityMaster.getEntity,
    getCardById: store.card.getCard,
    getNetbankingById: store.netBanking.getById,
    getMerchantById: store.merchantMapping.getById,
    getRoutingRuleById: store.routingRule.getRoutingFileById,
    getPackageById: store.package.getById,
    getPackage2InsById: store.packageToInstrument.getById,
    getPackage2MIdById: store.packageToMId.getById,
    getFilebyId: store.generateExcelFile.getById,
    getFeePackageById: store.feePackage.getById,
    getFeePackageToMidById: store.feePackageToMId.getById,
    fetchAllByFile: store.generateExcelFile.fetchAllByFile,
    getBankingCodeById: store.bankingCode.fetchBankingCodeById,
    download: store.fileUpload.download,
    fetchMidPayoutConfig: store.merchantPayoutConfig.fetchMidPayoutConfig,

    netbank: store.netBanking.bank,
    // getFileRoutingApprovalById: store.approvals.getFileRoutingApprovalById,
    merchant: store.merchantMapping.merchant,
    entity: store.entityMaster.getEntityView(),
    card: store.card.cardItem,
    packageData: store.package.package,
    packageInstrument: store.packageToInstrument.instrument,
    packageMId: store.packageToMId.packageMId,
    excelFile: store.generateExcelFile.excelFile,
    bankingCode: store.bankingCode.bankCode,
    feePackageData: store.feePackage.feePackage,
    feePackageToMidData: store.feePackageToMId?.feePackageMId,
    merchantPayoutConfigData: store.merchantPayoutConfig.config,
    crDrAdjustmentData: store.creditAdjustment.adjustment,
    payoutCmsData: store.payoutCms.settlement,
    riskConfigTxnData: store.riskConfig.transactions?.[0],
  }))

  const getDataByIdAction = (type: string) => {
    const getTypeData = {
      [ApprovalTypes.ENTITY]: getEntityById,
      [ApprovalTypes.CARDMAPPING]: getCardById,
      [ApprovalTypes.NETBANKING]: getNetbankingById,
      [ApprovalTypes.MERCHANT]: getMerchantById,
      [ApprovalTypes.BANKING_CODE]: getBankingCodeById,
      [ApprovalTypes.PACKAGE]: getPackageById,
      [ApprovalTypes.PACKAGE_INSTRUMENT]: getPackage2InsById,
      [ApprovalTypes.PACKAGE_MID]: getPackage2MIdById,
      [ApprovalTypes.GENERATE_EXCEL_FILE]: fetchAllByFile,
      [ApprovalTypes.FEE_PACKAGE]: getFeePackageById,
      [ApprovalTypes.FEE_PACKAGE_MID]: getFeePackageToMidById,
      [ApprovalTypes.MERCHANT_PAYOUT]: fetchMidPayoutConfig,
    }
    return getTypeData[type]
  }

  const getData = (type: string) => {
    const getTypeData = {
      [ApprovalTypes.ENTITY]: entity,
      [ApprovalTypes.CARDMAPPING]: card,
      [ApprovalTypes.NETBANKING]: netbank,
      [ApprovalTypes.MERCHANT]: merchant,
      [ApprovalTypes.BANKING_CODE]: bankingCode,
      [ApprovalTypes.PACKAGE]: packageData,
      [ApprovalTypes.PACKAGE_INSTRUMENT]: packageInstrument,
      [ApprovalTypes.PACKAGE_MID]: packageMId,
      [ApprovalTypes.GENERATE_EXCEL_FILE]: excelFile,
      [ApprovalTypes.FEE_PACKAGE]: feePackageData,
      [ApprovalTypes.FEE_PACKAGE_MID]: feePackageToMidData,
      [ApprovalTypes.MERCHANT_PAYOUT]: merchantPayoutConfigData,
      [ApprovalTypes.CREDIT_ADJUSTMENT]: crDrAdjustmentData,
      [ApprovalTypes.PAYOUT_CMS]: payoutCmsData,
      [ApprovalTypes.RISK_CONFIG_TXNS]: riskConfigTxnData,
    }
    return getTypeData[type] ?? {}
  }

  const [getDataByIdApi, getDataByIdApiHandler] = useLazyApiData(getDataByIdAction(type))
  const [lazyDownload] = useLazyApiData(download)

  useEffect(() => {
    if (approvalType?.toLowerCase().includes('update') || approvalType?.toLowerCase().includes('delete')) {
      const inputObj: any = {
        [ApprovalTypes.ENTITY]: data?.id,
        [ApprovalTypes.CARDMAPPING]: data?.id,
        [ApprovalTypes.MERCHANT_PAYOUT]: data?.mid,
      }
      getDataByIdAction(type) && getDataByIdApi(inputObj[type] ?? data.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    console.log(getDataByIdApiHandler)
    if (getDataByIdApiHandler.state === 'fulfilled') {
      setLoading(false)
    }
  }, [getDataByIdApiHandler])

  const renderAuditData = () => {
    const keysArray = Object.keys(data ?? {})
    return (
      <>
        <TextNew>Data</TextNew>
        {renderListItem(data, keysArray, type)}
      </>
    )
  }

  const renderCurrentData = () => {
    const cleanObj = clean(getData(type))
    const keysArray = Object.keys(cleanObj ?? {})
    return (
      (approvalType.toLowerCase().includes('update') || approvalType.toLowerCase().includes('delete')) && (
        <>
          {approvalType.toLowerCase().includes('update') && type !== 'FILE_ROUTING' && type !== 'FILE_UPLOAD' && (
            <TextNew>Current Data</TextNew>
          )}
          {getDataByIdApiHandler.state === 'pending' && (
            <Box padding={16}>
              <Loader />
            </Box>
          )}
          {renderListItem(getData(type), keysArray, type, lazyDownload)}
        </>
      )
    )
  }

  const renderUpdatedData = () => {
    const keysArray = Object.keys(data ?? {})
    return (
      (approvalType.toLowerCase().includes('update') || approvalType.toLowerCase().includes('new')) && (
        <>
          {approvalType.toLowerCase().includes('update') && <TextNew>Updated Data</TextNew>}
          {renderListItem(data, keysArray, type, lazyDownload)}
        </>
      )
    )
  }

  if (!data) {
    return null
  }
  return (
    <Box {...styles.approvalActionContainer}>
      <ModBox show={showCurrentData}>{renderCurrentData()}</ModBox>
      <ModBox show={showUpdatedData}>{renderUpdatedData()}</ModBox>
      <ModBox show={showAuditData}>{renderAuditData()}</ModBox>
      <SpacerNew direction={'vertical'} size={8} />
      <ModBox show={!showAuditData}>
        <ApprovalOrDecline
          type={type}
          approvalId={id}
          approvalType={approvalType}
          onSuccess={onSuccess}
          {...{ username }}
        />
      </ModBox>
    </Box>
  )
}
